/**
 * NotificationForm Component
 *
 * Form for composing notification content: title, body, link, and image.
 * Includes character counters, validation styling, and drag & drop image upload.
 */

import { useRef, useState, useCallback } from "react";
import {
  Type,
  AlignLeft,
  Link2,
  ImagePlus,
  X,
  Plus,
  MousePointerClick,
} from "lucide-react";
import type { ActionButton } from "../types";

interface NotificationFormProps {
  title: string;
  body: string;
  link: string;
  imagePreview: string | null;
  actionButtons: ActionButton[];
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onLinkChange: (value: string) => void;
  onImageChange: (dataUrl: string | null) => void;
  onActionButtonsChange: (buttons: ActionButton[]) => void;
}

const MAX_TITLE = 65;
const MAX_BODY = 240;
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export function NotificationForm({
  title,
  body,
  link,
  imagePreview,
  actionButtons,
  onTitleChange,
  onBodyChange,
  onLinkChange,
  onImageChange,
  onActionButtonsChange,
}: NotificationFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const processFile = useCallback(
    (file: File) => {
      setImageError(null);

      if (!ALLOWED_TYPES.includes(file.type)) {
        setImageError("Solo se permiten archivos PNG, JPG o WebP");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setImageError("La imagen no puede superar 1MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        onImageChange(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    },
    [onImageChange],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    setImageError(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        <Type size={18} className="text-violet-500" />
        <h3 className="font-semibold text-slate-900">Contenido del Mensaje</h3>
        <span className="text-xs text-slate-400 ml-auto">Paso 2</span>
      </div>

      <div className="space-y-5">
        {/* Title */}
        <div>
          <label className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Título de la notificación
            </span>
            <span
              className={`text-xs font-mono ${
                title.length > MAX_TITLE
                  ? "text-red-500 font-semibold"
                  : "text-slate-400"
              }`}
            >
              {title.length}/{MAX_TITLE}
            </span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value.slice(0, MAX_TITLE))}
            placeholder="Ej: ¡Oferta especial solo para vos!"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 
                            focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all duration-200"
          />
        </div>

        {/* Body */}
        <div>
          <label className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <AlignLeft size={14} className="text-slate-400" />
              Cuerpo del mensaje
            </span>
            <span
              className={`text-xs font-mono ${
                body.length > MAX_BODY
                  ? "text-red-500 font-semibold"
                  : "text-slate-400"
              }`}
            >
              {body.length}/{MAX_BODY}
            </span>
          </label>
          <textarea
            value={body}
            onChange={(e) => {
              onBodyChange(e.target.value.slice(0, MAX_BODY));
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            placeholder="Escribí el mensaje que verán tus usuarios..."
            rows={4}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 resize-none overflow-hidden
                            focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all duration-200"
          />
        </div>

        {/* Link */}
        <div>
          <label className="flex items-center gap-1.5 mb-2">
            <Link2 size={14} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-700">
              Enlace de destino
            </span>
            <span className="text-xs text-slate-400">(opcional)</span>
          </label>
          <input
            type="url"
            value={link}
            onChange={(e) => onLinkChange(e.target.value)}
            placeholder="https://tu-sitio.com/pagina"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
                            focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all duration-200"
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="flex items-center gap-1.5 mb-2">
            <ImagePlus size={14} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Imagen</span>
            <span className="text-xs text-slate-400">(opcional)</span>
          </label>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          {imagePreview ? (
            /* Image Preview */
            <div className="relative group rounded-xl overflow-hidden border border-slate-200">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-40 object-cover"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                <button
                  onClick={handleRemoveImage}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg hover:bg-red-50 absolute top-4 right-4  "
                >
                  <X size={18} className="text-red-500" />
                </button>
              </div>
              {/* File info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-[12px]">
                  Hacé clic en la ✕ para eliminar
                </p>
              </div>
            </div>
          ) : (
            /* Drop Zone */
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer group
                ${
                  isDragging
                    ? "border-violet-400 bg-violet-50/40 scale-[1.01]"
                    : "border-slate-200 hover:border-violet-300 hover:bg-violet-50/20"
                }
              `}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2 transition-colors
                ${
                  isDragging
                    ? "bg-violet-100"
                    : "bg-slate-100 group-hover:bg-violet-100"
                }
              `}
              >
                <ImagePlus
                  size={20}
                  className={`transition-colors ${
                    isDragging
                      ? "text-violet-500"
                      : "text-slate-400 group-hover:text-violet-500"
                  }`}
                />
              </div>
              <p className="text-xs text-slate-500">
                {isDragging
                  ? "Soltá la imagen aquí"
                  : "Arrastrá una imagen o hacé clic para subir"}
              </p>
              <p className="text-[10px] text-slate-300 mt-1">
                PNG, JPG, WebP hasta 1MB
              </p>
            </div>
          )}

          {/* Error message */}
          {imageError && (
            <p className="text-xs text-red-500 mt-2 animate-fade-in">
              {imageError}
            </p>
          )}
        </div>

        {/* Action Buttons Editor */}
        <div>
          <label className="flex items-center gap-1.5 mb-2">
            <MousePointerClick size={14} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-700">
              Botones de acción
            </span>
            <span className="text-xs text-slate-400">(opcional, máx. 3)</span>
          </label>

          <div className="space-y-2">
            {actionButtons.map((btn, index) => (
              <div
                key={index}
                className="flex items-center gap-2 animate-fade-in"
              >
                <input
                  type="text"
                  value={btn.label}
                  onChange={(e) => {
                    const updated = [...actionButtons];
                    updated[index] = {
                      ...updated[index],
                      label: e.target.value.slice(0, 20),
                    };
                    onActionButtonsChange(updated);
                  }}
                  placeholder="Texto del botón"
                  className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400
                    focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all duration-200"
                />
                <input
                  type="url"
                  value={btn.url}
                  onChange={(e) => {
                    const updated = [...actionButtons];
                    updated[index] = { ...updated[index], url: e.target.value };
                    onActionButtonsChange(updated);
                  }}
                  placeholder="URL (opcional)"
                  className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400
                    focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all duration-200"
                />
                <button
                  onClick={() => {
                    const updated = actionButtons.filter((_, i) => i !== index);
                    onActionButtonsChange(updated);
                  }}
                  className="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                >
                  <X size={16} />
                </button>
              </div>
            ))}

            {actionButtons.length < 3 && (
              <button
                onClick={() =>
                  onActionButtonsChange([
                    ...actionButtons,
                    { label: "", url: "" },
                  ])
                }
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-all"
              >
                <Plus size={14} />
                Agregar botón
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationForm;
